import React, { createContext, useContext, useState, useEffect, PropsWithChildren } from 'react';

type FontSize = 'normal' | 'medium' | 'large';

interface AccessibilityContextType {
  fontSize: FontSize;
  highContrast: boolean;
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
  toggleHighContrast: () => void;
  resetAccessibility: () => void;
}

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

const STORAGE_KEY_FONT_SIZE = 'accessibility_font_size';
const STORAGE_KEY_HIGH_CONTRAST = 'accessibility_high_contrast';

export const AccessibilityProvider: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const [fontSize, setFontSize] = useState<FontSize>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_FONT_SIZE);
    return (saved as FontSize) || 'normal';
  });

  const [highContrast, setHighContrast] = useState<boolean>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_HIGH_CONTRAST);
    return saved === 'true';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_FONT_SIZE, fontSize);

    // Classe na raiz (html): a tipografia do tema é em rem, então todo o texto escala junto
    document.documentElement.classList.remove('font-normal', 'font-medium', 'font-large');
    document.documentElement.classList.add(`font-${fontSize}`);
  }, [fontSize]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_HIGH_CONTRAST, String(highContrast));

    document.documentElement.classList.toggle('high-contrast', highContrast);
  }, [highContrast]);

  const increaseFontSize = () => {
    setFontSize((current) => {
      if (current === 'normal') return 'medium';
      if (current === 'medium') return 'large';
      return 'large'; // Already at max
    });
  };

  const decreaseFontSize = () => {
    setFontSize((current) => {
      if (current === 'large') return 'medium';
      if (current === 'medium') return 'normal';
      return 'normal'; // Already at min
    });
  };

  const toggleHighContrast = () => {
    setHighContrast((current) => !current);
  };

  const resetAccessibility = () => {
    setFontSize('normal');
    setHighContrast(false);
  };

  return (
    <AccessibilityContext.Provider
      value={{
        fontSize,
        highContrast,
        increaseFontSize,
        decreaseFontSize,
        toggleHighContrast,
        resetAccessibility,
      }}
    >
      {children}
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (context === undefined) {
    throw new Error('useAccessibility must be used within AccessibilityProvider');
  }
  return context;
};
